import {
  Chip,
  Divider,
  Icon,
  Skeleton,
} from "@mui/material";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Proposal } from "generated/graphql";
import dayjs from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utcPlugin from "dayjs/plugin/utc";
import { useContext, useMemo, useState } from "react";
import BitumioContext from "providers/BitumioContext";
import LogActivityModal from "modules/activities/LogActivityModal/LogActivityModal";
import { ProposalActivityRow, useProposalActivities } from "hooks/activities/useProposalActivities";
import CreateTaskModal from "modules/tasks/CreateTaskModal/CreateTaskModal";

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

const ACTIVITY_TYPE_META: Record<string, { icon: string; label: string }> = {
  CALL: { icon: "call", label: "Call" },
  TEXT: { icon: "sms", label: "Text Message" },
  EMAIL: { icon: "email", label: "Email" },
  MEETING: { icon: "event", label: "Meeting" },
  NOTE: { icon: "sticky_note_2", label: "Note" },
  PROPOSAL_SENT: { icon: "send", label: "Proposal sent" },
  PROPOSAL_VIEWED: { icon: "visibility", label: "Proposal viewed" },
  PROPOSAL_SIGNED: { icon: "draw", label: "Proposal signed" },
  EMAIL_OPENED: { icon: "mark_email_read", label: "Email opened" },
  EMAIL_CLICKED: { icon: "ads_click", label: "Email clicked" },
  STATUS_CHANGE: { icon: "change_circle", label: "Status change" },
};

type UnifiedItem = {
  id: string;
  source: "USER" | "SYSTEM";
  type: string;
  summary: string;
  body?: string | null;
  occurredAt: string;
};

function iconForType(type: string) {
  const t = (type ?? "").toUpperCase();
  return ACTIVITY_TYPE_META[t]?.icon ?? "bolt";
}

function labelForType(type: string) {
  const t = (type ?? "").toUpperCase();
  return ACTIVITY_TYPE_META[t]?.label ?? "Activity";
}

function mapProposalEventActionToType(action: string) {
  const a = (action ?? "").toLowerCase();
  if (a === "sent" || a === "resent") return "PROPOSAL_SENT";
  if (a === "viewed") return "PROPOSAL_VIEWED";
  if (a === "email_opened") return "EMAIL_OPENED";
  if (a === "email_clicked") return "EMAIL_CLICKED";
  return action?.toUpperCase?.() ?? "SYSTEM";
}
function toMillis(value: string, tz: string, source: "USER" | "SYSTEM") {
  if (!value) return 0;

  const hasZone =
    /[zZ]$/.test(value) ||
    /[+-]\d{2}:\d{2}$/.test(value) ||
    /[+-]\d{4}$/.test(value);

  const parsed = hasZone
    ? dayjs(value)
    : source === "SYSTEM"
      ? dayjs.utc(value)
      : dayjs.tz(value, tz);

  return parsed.isValid() ? parsed.valueOf() : 0;
}

function formatOccurredAt(value: string, timezone: string) {
  return dayjs.utc(value).tz(timezone).format("MMM D, YYYY [at] h:mma");
}

export default function ProposalActivityLog({ proposal }: { proposal: Proposal }) {
  const { signedInUser } = useContext(BitumioContext);

  const timezone = useMemo(() => {
    return signedInUser.organization?.timezone ?? dayjs.tz.guess() ?? "America/Chicago";
  }, [signedInUser]);

  const proposalId = (proposal as any)?.id as string | undefined;
  const proposalDisplayId =
    (proposal as any)?.versionWithPrefix ??
    (proposal as any)?.externalId ??
    proposalId;

  const contactId = (proposal as any)?.contactId ?? (proposal as any)?.contact?.id ?? null;
  const opportunityId = (proposal as any)?.opportunityId ?? (proposal as any)?.opportunity?.id ?? null;
  const companyId = (proposal as any)?.companyId ?? (proposal as any)?.company?.id ?? null;

  const { activities: manualAll, loading, error } = useProposalActivities({
    contactId,
    opportunityId,
    first: 50,
    page: 1,
  });

  const manualForThisProposal: ProposalActivityRow[] = useMemo(() => {
    if (!proposalId && !opportunityId) return [];
    return manualAll.filter((a) => {
      const byProposal = !!proposalId && a?.proposal?.id === proposalId;
      const byOpp = !!opportunityId && a?.opportunity?.id === opportunityId;
      return byProposal || byOpp;
    });
  }, [manualAll, proposalId, opportunityId]);

  const systemItems: UnifiedItem[] = useMemo(() => {
    const items: UnifiedItem[] = [];

    const events = ((proposal as any)?.events ?? []) as Array<{
      id: string;
      action: string;
      createdAt: string;
    }>;

    for (const e of events) {
      const type = mapProposalEventActionToType(e.action);
      items.push({
        id: `proposalEvent:${e.id}`,
        source: "SYSTEM",
        type,
        summary: labelForType(type),
        occurredAt: e.createdAt,
      });
    }

    const statusChanges = ((proposal as any)?.statusChanges ?? []) as Array<{
      id?: string;
      status: string;
      changeAt: string;
    }>;

    for (const s of statusChanges) {
      items.push({
        id: `statusChange:${s.id ?? `${s.status}:${s.changeAt}`}`,
        source: "SYSTEM",
        type: "STATUS_CHANGE",
        summary: `Status changed to ${s.status}`,
        occurredAt: s.changeAt,
      });
    }

    return items;
  }, [proposal]);

  const unified: UnifiedItem[] = useMemo(() => {
    const userItems: UnifiedItem[] = manualForThisProposal.map((a) => ({
      id: `activity:${a.id}`,
      source: "USER",
      type: a.type,
      summary: a.summary,
      body: a.body ?? null,
      occurredAt: a.occurredAt,
    }));

    const all = [...systemItems, ...userItems];

    all.sort((a, b) => {
      const ad = toMillis(a.occurredAt, timezone, a.source);
      const bd = toMillis(b.occurredAt, timezone, b.source);
      if (bd !== ad) return bd - ad;
      return a.id.localeCompare(b.id);
    });


    return all;
  }, [manualForThisProposal, systemItems, timezone]);

  const [open, setOpen] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);

  return (
    <MDBox width="100%">
      <MDBox pt={2} px={2} display="flex" alignItems="center" justifyContent="space-between">
        <MDTypography variant="h6" fontWeight="medium">
          Activity
        </MDTypography>

        <MDBox display="flex" alignItems="center" gap={1}>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => setOpen(true)}
            disabled={!contactId || !proposalId}
            startIcon={<Icon>add</Icon>}
          >
            Log Activity
          </MDButton>

          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => setCreateTaskOpen(true)}
            disabled={!proposalId}
            startIcon={<Icon>add</Icon>}
          >
            Create Task
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox p={2}>
        <MDBox opacity={0.3}>
          <Divider />
        </MDBox>

        {!contactId ? (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              No contact selected.
            </MDTypography>
          </MDBox>
        ) : !proposalId ? (
          <MDBox mt={2}>
            <MDTypography variant="body2" color="text">
              Proposal not loaded.
            </MDTypography>
          </MDBox>
        ) : error ? (
          <MDBox mt={2}>
            <MDAlert color="error">{error.message}</MDAlert>
          </MDBox>
        ) : (
          <MDBox
            mt={2}
            sx={{
              maxHeight: "60vh",
              overflowY: "auto",
              pr: 1,
            }}
          >
            {loading ? (
              <>
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
                <Skeleton height={40} />
              </>
            ) : unified.length === 0 ? (
              <MDTypography variant="body2" color="text">
                No activities yet.
              </MDTypography>
            ) : (
              unified.map((a) => (
                <MDBox
                  key={a.id}
                  sx={{
                    border: "1px solid rgba(0,0,0,0.12)",
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <Icon fontSize="small">{iconForType(a.type)}</Icon>

                    <MDTypography variant="body2" fontWeight="medium">
                      {labelForType(a.type)}: {a.summary}
                    </MDTypography>

                    <MDBox flex={1} />

                    <Chip
                      size="small"
                      label={a.source === "SYSTEM" ? "System" : "User"}
                      sx={{
                        fontWeight: 500,
                        height: 20,
                        backgroundColor:
                          a.source === "SYSTEM" ? "rgba(0,0,0,0.08)" : "rgba(76, 175, 80, 0.18)",
                        color: a.source === "SYSTEM" ? "rgba(0,0,0,0.75)" : "#2e7d32",
                      }}
                    />
                  </MDBox>

                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 0.5 }}>
                    {formatOccurredAt(a.occurredAt, timezone)}
                  </MDTypography>

                  {a.body ? (
                    <MDTypography variant="body2" color="text" sx={{ mt: 1 }}>
                      {a.body}
                    </MDTypography>
                  ) : null}
                </MDBox>
              ))
            )}
          </MDBox>
        )}
      </MDBox>

      <LogActivityModal
        open={open}
        onClose={() => setOpen(false)}
        contactId={contactId}
        companyId={companyId}
        opportunityId={opportunityId}
        proposalId={proposalId}
        timezone={timezone}
        requiresProposal
      />
      <CreateTaskModal
        open={createTaskOpen}
        onClose={() => setCreateTaskOpen(false)}
        prefillRelated={
          proposalId
            ? {
                type: "proposal",
                selected: {
                  id: String(proposalId),
                  label: `Proposal #${String(proposalDisplayId)}`,
                  companyId: companyId ? String(companyId) : null,
                  contactId: contactId ? String(contactId) : null,
                },
              }
            : undefined
        }
      />
    </MDBox>
  );
}
